import React, { useState } from 'react';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import useStyles from '@front10/helpers/dist/UseStyles';
import { isSSR } from '@front10/utils';
import withCommonEMContainer from '../_commons/CommonEMContainer/CommonEMHospitalityContainer';

//-------------------------------------
// Basic components here
//-------------------------------------
import Row from '../../elements/Row';
import Col from '../../elements/Col';
import Box from '../../elements/Box';
import Text from '../../elements/Text';

import Disclaimer from '../../others/Disclaimer';
import RatesCalendar from '../../controls/FareCalendar';
import MonthNavigationDropdown from '../../controls/FareFinderCalendar/atoms/MonthNavigationDropdown';
import DayComponentInstance from '../../items/RateCard';
import InfoNavigation from '../PropertyLowRatesCalendarContainer/atoms/InfoNavigation';
import Legend from '../../others/Legend/LegendHotelLowRateCalendar';
import Loading from '../../others/Loading';
import NoFaresBehavior from '../../others/NoFaresBehavior/NoFaresBehavior';
import Skeleton from '../../others/Skeleton/SkeletonHotelLowRateCalendarMobile';
import ContainerErrorAlert from '../../alerts/ContainerErrorAlert';

//-------------------------------------
// Every mundo integration
//-------------------------------------
import { getHistograms } from '../../../_integrations/everymundo/rates';
import { getProperties } from '../../../_integrations/everymundo/locationsEM';

//-------------------------------------
// Commons functions
//-------------------------------------
import { useFilters } from '../_commons/hospitality/filters';
import { useFareItemProps } from '../_commons/tracking';
import InlineFilters from '../../controls/HospitalityInlineFilters/PropertyInlineFilter';
import SecondaryFilters from '../../controls/HospitalitySecondaryFilters/PropertySecondaryFilters';
import useStayLengthDisabledSettings from '../PropertyLowRatesCalendarContainer/filters/useStayLengthDisabled';
import { getResultsLength } from '../_commons/calendar';

import PropertyAlert from '../PropertyLowRatesCalendarContainer/atoms/PropertyAlert';
import useFiltersDisableSettings from '../_commons/hospitality/useFiltersDisabled';

import prePopulationDefaultProps from '../_commons/prepopulationDefaultProps/histogram';
import HeaderContainer from '../../elements/HeaderContainer';

const getRatesTags = () => {};

function PropertyLowRatesCalendarContainerMobile(props) {
  const {
    prePopulation,
    integrationSettings,
    meta,
    ratesCalendar,
    legend,
    loading,
    direction,
    translate,
    initialHistograms,
    getProperties: getPropertiesFn,
    getHistograms: getHistogramsFn,
    fareItemOnClickAction,
    showDisclaimer,
    showFilters,
    showSecondaryFilters,
    secondaryFilters,
    showLegend,
    skeletonSettings,
    disclaimerContentSettings,
    mainContentSettings,
    legendContentSettings,
    filterContentSettings,
    alert,
    minInitialResults,
    theme,
    filters,
    daysLeftForAutomatedRollover,
    disclaimer,
    noResultsPlaceHolder,
    alertInfo,
    alertInfoTextSettings,
    enableSEO = true
  } = props;

  const { options = [] } = secondaryFilters.stayLengthSettings;
  const [showThresholdDay, setThresholdDay] = useState(!!daysLeftForAutomatedRollover);

  const [className, style] = useStyles(props, { direction, theme });
  const myStyle = Object.assign({ minHeight: '600px' }, style);

  // Define filter props using deprecated container props (for backwards compatibility)
  // Don't rely on this props for new configurations, they will be removed in a future version
  const filtersConfig = {
    containerSettings: filterContentSettings,
    ...filters
  };
  const { months = 3, defaultMonth = 1 } = ratesCalendar;

  const initialMonth = dayjs().add(prePopulation.dateRange.start, 'day').toDate();

  const daysLeftForAutomatedRolloverEnable =
    daysLeftForAutomatedRollover &&
    showThresholdDay &&
    dayjs(initialMonth).date() > dayjs(initialMonth).daysInMonth() - daysLeftForAutomatedRollover;

  const rateTags = secondaryFilters?.rateTagsSettings?.values;

  const {
    fares: { match, ...fares },
    dispatch,
    tracking,
    retry,
    ...filtersState
  } = useFilters({
    filter: {
      prePopulation,
      integrationSettings,
      meta,
      getProperties: getPropertiesFn,
      maxItemsAmount: 1,
      nextMonth: daysLeftForAutomatedRolloverEnable,
      defaultMonth: defaultMonth <= months ? defaultMonth : 1
    },
    initialData: initialHistograms,
    rateTagsData: rateTags,
    stayLengthData: secondaryFilters?.stayLengthSettings?.options,
    getFares: getHistogramsFn,
    getRatesTags,
    faresTransformationLogic: 'histogram',
    minInitialResults,
    showNoResultsPlaceholder: false,
    availableFilters: showFilters ? filtersConfig.availableFilters : [],
    availableSecondaryFilter: secondaryFilters ? secondaryFilters.availableFilters : [],
    enabledHistogram: true,
    getResultsLength
  });

  const { dateRange, stayLength, ratesTags, property } = filtersState;
  const dayCard = useFareItemProps(ratesCalendar.dayCard, dispatch, fareItemOnClickAction);

  const stayLengthInfo = React.useMemo(() => {
    if (Boolean(stayLength)) {
      const value = options.find(
        (i) => JSON.stringify(i.value) === JSON.stringify(stayLength.value)
      );
      return value
        ? translate(value.secondaryLabel, 'any stay length')
        : translate('cmp-property-low-rates-any', 'any stay length');
    } else {
      return translate('cmp-property-low-rates-any', 'any stay length');
    }
  }, [stayLength, options, translate]);

  if (isSSR() && !Boolean(enableSEO)) {
    return null;
  }

  if (match('HIDDEN')) {
    return <NoFaresBehavior />;
  }

  const changeMonth = (month) => {
    dispatch({ type: 'MONTH_CHANGE', payload: month });
    setThresholdDay(false);
  };

  function handleSeeMoreDeals() {
    tracking({ type: 'MORE_DEALS' });
    return true;
  }

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { stayLengthDisabledSettings, stayLengthInfoLabel } = useStayLengthDisabledSettings(
    prePopulation.disabledStayLength,
    { ...secondaryFilters.stayLengthSettings },
    {
      ratesTags: ratesTags.selectedItems
    }
  );

  const isPropertySelected = Boolean(property.property.value);

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const secondaryFiltersDisableSettings = useFiltersDisableSettings(
    { ...filters, ...secondaryFilters },
    {
      ratesTags: !isPropertySelected,
      stayLength: !isPropertySelected
    }
  );

  const renderAlertInfo = !isPropertySelected && (
    <Box {...alertInfo}>
      <Text {...alertInfoTextSettings}>
        {translate(
          'cmp-property-low-rates-calendar-alert-info',
          'Please input the property in order to display average daily rates.'
        )}
      </Text>
    </Box>
  );

  return (
    <>
      {match('SKELETON') && <Skeleton {...skeletonSettings} width="100" />}
      {match('LOADING') && <Loading {...loading} />}
      <Box
        position="relative"
        className={`${className} ${PropertyLowRatesCalendarContainerMobile.componentName}`}
        style={myStyle}
        dataTestId="lowFareCalendarContainer"
      >
        <HeaderContainer
          defaultTitleValue={'Find the best rate for your next getaway'}
          defaultSubtitleValue={'Discover the best deals with our low rate calendar.'}
          handleSeeMoreDeals={handleSeeMoreDeals}
          {...props}
        />
        {showFilters && (
          <InlineFilters
            className={`${className} ${PropertyLowRatesCalendarContainerMobile.componentName}`}
            style={myStyle}
            dataTestId="faresCarouselBackgroundImageContainerMobile"
            // header={header}
            {...filtersConfig}
            {...filtersState}
          ></InlineFilters>
        )}
        {showSecondaryFilters && (
          <SecondaryFilters
            {...filtersConfig}
            {...secondaryFilters}
            {...stayLengthDisabledSettings}
            {...secondaryFiltersDisableSettings}
            {...filtersState}
            availableFilters={filtersState.availableSecondaryFilter}
            filter={{ ...{ prePopulation, integrationSettings, meta } }}
            loading={match('SKELETON')}
          />
        )}

        {renderAlertInfo}
        <Row {...mainContentSettings} style={{ position: 'relative' }}>
          {match('ERROR') && (
            <Col>
              <ContainerErrorAlert {...alert} tryAgain={retry} />
            </Col>
          )}
          {match('WAITING_FOR_PROPERTY') && (
            <Col>
              <PropertyAlert {...noResultsPlaceHolder} />
            </Col>
          )}
          {match('COMPLETE') && (
            <>
              <Col justifyContent="center" display="flex">
                <RatesCalendar
                  {...ratesCalendar}
                  dayCard={dayCard}
                  initialMonth={initialMonth}
                  currentMonth={dateRange.value.start}
                  data={fares.data}
                  NavComponentInstance={MonthNavigationDropdown}
                  renderNavInfo={
                    <InfoNavigation
                      stayLength={stayLengthInfoLabel !== '' ? stayLengthInfoLabel : stayLengthInfo}
                      {...ratesCalendar.navInfoSettings}
                    />
                  }
                  onSelectMonth={changeMonth}
                  loading={match('LOADING')}
                  DayComponentInstance={DayComponentInstance}
                  isDesktop={false}
                />
              </Col>
              {showLegend && (
                <Col md="12" {...legendContentSettings}>
                  <Legend {...legend} />
                </Col>
              )}
            </>
          )}
        </Row>
        {showDisclaimer && (
          <Row {...disclaimerContentSettings}>
            <Col>
              <Disclaimer
                floatLeft
                labelKey="cmp-property-low-rates-calendar-container-disclaimer"
                {...disclaimer}
              />
            </Col>
          </Row>
        )}
      </Box>
    </>
  );
}

PropertyLowRatesCalendarContainerMobile.propTypes = {
  /**
   * headerContentSettings props
   */
  headerContentSettings: PropTypes.objectOf(PropTypes.any),
  /**
   * Props of alerts
   */
  alert: PropTypes.objectOf(PropTypes.any),
  /**
   * Property Component
   */
  property: PropTypes.objectOf(PropTypes.any),
  /**
   * To be used to set if t container is a controlled one
   */
  containerType: PropTypes.string,
  /**
   * Direction of the component rtl o ltr
   */
  direction: PropTypes.oneOf(['rtl', 'ltr']),
  /**
   * Disclaimer Component
   */
  disclaimer: PropTypes.objectOf(PropTypes.any),
  /**
   * disclaimerContentSettings props
   */
  disclaimerContentSettings: PropTypes.objectOf(PropTypes.any),
  /**
   * RatesCalendar Component props
   */
  ratesCalendar: PropTypes.objectOf(PropTypes.any),
  /**
   * Possible interactions will be Popup or Direct Deeplink
   */
  fareItemInteractionType: PropTypes.oneOf(['popup', 'deeplink']),
  /**
   * Handle the action when click on card
   */
  fareItemOnClickAction: PropTypes.func,
  /**
   * filterContentSettings props
   */
  filterContentSettings: PropTypes.objectOf(PropTypes.any),
  /**
   * Filters settings
   */
  filters: PropTypes.objectOf(PropTypes.any),
  /**
   * Function for request to obtain propertys
   */
  getProperties: PropTypes.func,
  getRatesTags: PropTypes.func,
  /**
   * Function for request to obtain histograms
   */
  getHistograms: PropTypes.func,
  /**
   * Hide the component if the ammount of initial results is lower than this value
   */
  minInitialResults: PropTypes.number,
  /**
   * Used to prepopulate the component
   */
  initialHistograms: PropTypes.arrayOf(PropTypes.any),
  /**
   * All integration configurations
   */
  integrationSettings: PropTypes.objectOf(PropTypes.any),
  /**
   * legend Component props
   */
  legend: PropTypes.objectOf(PropTypes.any),
  /**
   * legend content settings
   */
  legendContentSettings: PropTypes.objectOf(PropTypes.any),
  /**
   * Loading Component
   */
  loading: PropTypes.objectOf(PropTypes.any),
  /**
   * mainContentSettings props
   */
  mainContentSettings: PropTypes.objectOf(PropTypes.any),
  /**
   * Props of noResultsPlaceHolder
   */
  noResultsPlaceHolder: PropTypes.objectOf(PropTypes.any),
  /**
   * Origin Component
   */
  origin: PropTypes.objectOf(PropTypes.any),
  /**
   * Page Context
   */
  pageContext: PropTypes.shape({
    deviceCategory: PropTypes.string,
    siteEdition: PropTypes.string
  }),
  /**
   * Component PrePopulation
   */
  prePopulation: PropTypes.objectOf(PropTypes.any),
  /**
   * Determine if the component will show disclaimer
   */
  showDisclaimer: PropTypes.bool,
  /**
   * showFilters
   */
  showFilters: PropTypes.bool,
  /**
   * Show exchange button or not
   */
  showSwitch: PropTypes.bool,
  /**
   * Determine if the component will show the travelClass filter
   */
  showTravelClassFilter: PropTypes.bool,
  /**
   * showLegend
   */
  showLegend: PropTypes.bool,
  /**
   * Determine if the component will show the title
   */
  showTitle: PropTypes.bool,
  /**
   * SwitchOriginDestination Component
   */
  switchOriginDestination: PropTypes.objectOf(PropTypes.any),
  /**
   * SkeletonSettings Component
   */
  skeletonSettings: PropTypes.objectOf(PropTypes.any),
  /**
   * Title Component
   */
  title: PropTypes.objectOf(PropTypes.any),
  /**
   * titleKey of the component
   */
  titleKey: PropTypes.string,
  /**
   * Function to translate the component
   */
  translate: PropTypes.func,
  /**
   * travelClass options
   */
  travelClassOptions: PropTypes.objectOf(PropTypes.any),
  /**
   * travelClass of the component
   */
  travelClass: PropTypes.objectOf(PropTypes.any),
  /**
   * Parameters tracking
   */
  tracking: PropTypes.objectOf(PropTypes.any),
  /**
   * LocalizationData via moment to be used as a date translator
   * https://github.com/moment/moment/blob/develop/src/locale/es.js
   */
  localizationData: PropTypes.objectOf(PropTypes.any),
  /**
   * Theme object on the component
   */
  theme: PropTypes.objectOf(PropTypes.any),
  /**
   * Metadata configuration
   */
  meta: PropTypes.objectOf(PropTypes.any),

  /**
   * Show the SubTitle Component
   */
  showSubTitle: PropTypes.bool,
  /**
   * subHeaderContentSettings props
   */
  subHeaderContentSettings: PropTypes.objectOf(PropTypes.any),
  /**
   * titleKey of the component
   */
  subTitleKey: PropTypes.string,
  /**
   * SubTitle Component
   */
  subTitleSettings: PropTypes.objectOf(PropTypes.any),
  /**
   * daysLeftForAutomatedRollover indicate how many days before the end of the month it should show the calendar of low rates for the next month
   */
  daysLeftForAutomatedRollover: PropTypes.number,
  /**
   * navInfoSettings props
   */
  navInfoSettings: PropTypes.objectOf(PropTypes.any)
};
PropertyLowRatesCalendarContainerMobile.defaultProps = {
  headerContentSettings: {},
  alert: {
    bgColor: 'secondary-4',
    textAlign: 'center',
    padding: '3',
    rounded: '@theme:card.rounded'
  },
  containerType: 'stateful',
  property: {},
  direction: 'ltr',
  disclaimer: {
    fontSize: '3'
  },
  disclaimerContentSettings: {
    marginTop: '2'
  },
  ratesCalendar: {},
  fareItemInteractionType: 'popup',
  fareItemOnClickAction: null,
  filterContentSettings: {
    marginTop: '4'
  },
  filters: {},
  getRatesTags: () => {},
  getProperties,
  getHistograms,
  minInitialResults: null,
  initialHistograms: null,
  integrationSettings: {},
  legend: {},
  legendContentSettings: {
    marginTop: '2'
  },
  loading: {},
  mainContentSettings: {
    marginTop: '4'
  },
  noResultsPlaceHolder: {
    bgColor: 'light'
  },
  pageContext: {
    deviceCategory: 'DESKTOP',
    siteEdition: 'en_HK'
  },
  prePopulation: prePopulationDefaultProps,
  showDisclaimer: true,
  showFilters: true,
  showTravelClassFilter: false,
  showLegend: true,
  showTitle: true,
  skeletonSettings: {
    style: { backgroundColor: 'white', paddingBottom: '1.5rem' }
  },
  title: {},
  titleKey: 'cmp-property-low-rates-calendar-container-title',
  translate: () => {},
  tracking: {
    passengerCount: 2,
    eventType: 'em-cmp-property-low-rates-calendar'
  },
  localizationData: null,
  theme: null,
  meta: null,
  showSubTitle: false,
  subHeaderContentSettings: {},
  subTitleKey: 'cmp-property-low-rates-calendar-sub-title',
  subTitleSettings: {},
  daysLeftForAutomatedRollover: 5,
  navInfoSettings: {}
};
PropertyLowRatesCalendarContainerMobile.componentName = 'PropertyLowRatesCalendarContainerMobile';

// HOC with common functionality to all containers
export default withCommonEMContainer(PropertyLowRatesCalendarContainerMobile);
